import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, FileText, BadgeCheck, ShieldCheck, MessagesSquare, Leaf } from "lucide-react";

const COACH_IMG = "/coach-nikhil.webp";

const LINES = [
  { text: "REAL FOOD.", cls: "text-white" },
  { text: "REAL SCIENCE.", cls: "text-white" },
  { text: "ZERO GUESSWORK.", cls: "text-outline" },
  { text: "REAL RESULTS.", cls: "text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-amber-400" },
];

const BADGES = [
  { icon: BadgeCheck, label: "NHCA Certified Dietitian & Nutritionist" },
  { icon: Leaf, label: "Whole-Food First Approach" },
  { icon: ShieldCheck, label: "Blood-Work-Backed Supplementation" },
  { icon: MessagesSquare, label: "200+ Clients · 7 Years" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
      <div className="absolute inset-0 hero-grid" aria-hidden="true" />
      <motion.div
        style={{ y: yGlow }}
        aria-hidden="true"
        className="absolute -top-40 left-1/2 -translate-x-1/2 h-[560px] w-[900px] rounded-full bg-accent/10 blur-[140px]"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-emerald-400 mb-6 flex items-center gap-3"
          >
            <span className="h-2 w-2 rotate-45 bg-gold" />
            Nikhil Shrirao · Certified Dietitian & Wellness Coach
          </motion.p>
          <h1 className="font-display font-black uppercase tracking-tight leading-[0.92] text-4xl sm:text-5xl lg:text-6xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
          >
            A whole-food first coaching practice. Low glycaemic index home food, measured correctly, with daily
            accountability — and zero unnecessary supplements. Anything clinical is recommended only when your
            medical history or blood markers actually demand it. Certified by the National Health Care Academy.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#intake-form"
              data-testid="hero-cta-start-transformation"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-br from-accent to-accent-dim text-ink font-extrabold uppercase tracking-wide transition-all duration-300 hover:shadow-glow-emerald hover:-translate-y-0.5"
            >
              Start Transformation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#sample-plan"
              data-testid="hero-cta-sample-plan"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-gold/50 text-gold font-bold uppercase tracking-wide transition-all duration-300 hover:bg-gold/10 hover:-translate-y-0.5"
            >
              <FileText className="h-4 w-4" />
              See A Real Plan
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-12 flex flex-wrap gap-3"
          >
            {BADGES.map((b, i) => (
              <span
                key={b.label}
                data-testid={`hero-badge-${i}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-panel/80 border border-line text-xs font-semibold text-slate-200 transition-colors duration-300 hover:border-accent/50"
              >
                <b.icon className="h-3.5 w-3.5 text-emerald-400" />
                {b.label}
              </span>
            ))}
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 bg-gradient-to-tr from-accent/25 via-transparent to-gold/20 blur-3xl rounded-full"
            />
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl border border-white/10 ring-1 ring-black/40 bg-panel">
              <img
                src={COACH_IMG}
                alt="Nikhil Shrirao, NHCA Certified Dietitian & Nutritionist"
                className="absolute inset-0 h-full w-full object-cover object-center"
                loading="eager"
                data-testid="hero-coach-photo"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent opacity-70" />
            </div>
            <span className="absolute bottom-4 right-4 font-mono text-[11px] tracking-[0.2em] text-slate-200 bg-ink/80 border border-line px-3 py-1.5 rounded-full backdrop-blur">
              @FITNESS_FANATIC_NIK
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}